import SpeakButton from "@/components/SpeakButton";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { Sparkles, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { makeApi, aiErrorText, coinToast } from "@/lib/api";
import { useT } from "@/i18n";
import { LumiMascot } from "@/components/Mascots";
import { PRODUCTS, BIZ_TEXT } from "@/components/business/products";
import ProductPicker from "@/components/business/ProductPicker";
import NameResults from "@/components/business/NameResults";
import MyBusinesses from "@/components/business/MyBusinesses";

export default function BusinessBuilder() {
  const { lang } = useT();
  const tx = BIZ_TEXT[lang];
  const { getToken, isSignedIn } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const [selected, setSelected] = useState(null);
  const [other, setOther] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [ideas, setIdeas] = useState([]);

  useEffect(() => {
    if (isSignedIn) api.get("/business/ideas").then((r) => setIdeas(r.data.ideas)).catch(() => {});
  }, [api, isSignedIn]);

  const product = selected === "other" ? other.trim() : PRODUCTS.find((p) => p.id === selected)?.[lang];

  const generate = async () => {
    setLoading(true);
    try {
      const r = await api.post("/business/names", { product, language: lang });
      setResult(r.data);
      coinToast(r.data.coins_awarded, lang);
    } catch (e) {
      toast.error(aiErrorText(e, lang, tx.error));
    } finally {
      setLoading(false);
    }
  };

  const pick = async (n) => {
    try {
      const r = await api.post("/business/ideas", { product, name: n.name, slogan: n.slogan });
      setIdeas((list) => [r.data, ...list]);
      toast.success(tx.saved);
    } catch (e) {
      toast.error(aiErrorText(e, lang, tx.error));
    }
  };

  const remove = async (id) => {
    await api.delete(`/business/ideas/${id}`);
    setIdeas((list) => list.filter((i) => i.id !== id));
  };

  return (
    <section className="learn-bg rounded-[2rem] border-4 border-white shadow-xl p-5 sm:p-8 mt-8" data-testid="business-builder">
      <div className="flex items-center gap-4">
        <LumiMascot className="w-20 sm:w-28 shrink-0" />
        <h2 className="font-fredoka text-2xl sm:text-4xl font-bold bubble-text text-white">{tx.title}</h2>
      </div>
      <div className="mt-6">
        <ProductPicker selected={selected} onSelect={(id) => { setSelected(id); setResult(null); }} other={other} setOther={setOther} />
      </div>
      {product && (
        <div className="mt-8">
          <div className="flex flex-wrap items-center gap-2"><h3 className="font-fredoka text-xl sm:text-2xl font-bold text-blue-950">{tx.step2}</h3><SpeakButton compact mascot="lumi" text={tx.step2} testId="ask-business-step2" /></div>
          {isSignedIn ? (
            <button
              type="button"
              onClick={generate}
              disabled={loading}
              className="glossy-btn mt-4 bg-yellow-300 text-blue-900 font-fredoka font-bold text-xl px-8 py-3 rounded-full inline-flex items-center gap-2 disabled:opacity-70"
              data-testid="biz-generate-btn"
            >
              {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : result ? <RefreshCw className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
              {loading ? tx.thinking : result ? tx.again : tx.power}
            </button>
          ) : (
            <Link to="/sign-in" className="glossy-btn inline-block mt-4 bg-white text-blue-900 font-bold px-6 py-3 rounded-full" data-testid="biz-signin-btn">
              {tx.signIn}
            </Link>
          )}
          {result && <NameResults result={result} onPick={pick} />}
        </div>
      )}
      <MyBusinesses ideas={ideas} onDelete={remove} />
    </section>
  );
}
